import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { UserPermissionKeyValue } from 'app/security/authorization/user-permission-key-value';
import { UserPermissionKeys } from 'app/security/authorization/user-permission-keys';
import { BehaviorSubject, Subject } from 'rxjs';
import { Identity } from '../viewmodels/login/identity';
import { PermissionSet } from './auth/permission-set';

@Injectable()
export class PermissionsService {
  public Permissions: PermissionSet = new PermissionSet();

  private userPermissionKeyValuePairsSource: BehaviorSubject<UserPermissionKeyValue[]> = new BehaviorSubject<UserPermissionKeyValue[]>([]);
  public userPermissionKeyValuePairs = this.userPermissionKeyValuePairsSource.asObservable();

  public PermissionsChangedEvent: Subject<PermissionSet> = new Subject<PermissionSet>();

  public readonly userPermissionKeys: UserPermissionKeys = new UserPermissionKeys();

  public SetPermissions(identity: Identity): void {

    this.setUserPermissions(identity);

    this.Permissions.isSecurity = identity.isSecurity;

    this.PermissionsChangedEvent.next(this.Permissions);
  }

  private setUserPermissions(identity: Identity): void {
    const userPermissionKeyValuePairs = this.setDefaultUserPermissionKeyValueList();

    if (identity && identity.permissions) {
      const { permissions } = identity;

      permissions.forEach(p => {
        const keyValueFound = userPermissionKeyValuePairs.find(keyValue => keyValue.key === p);

        if (keyValueFound) {
          keyValueFound.value = true;
        }
      });
    }

    this.setUserFlagsPermissions(userPermissionKeyValuePairs, identity);

    this.userPermissionKeyValuePairsSource.next(userPermissionKeyValuePairs);
  }

  private setUserFlagsPermissions(userPermissionKeyValuePairs: UserPermissionKeyValue[], identity: Identity): void {
    if (identity) {
      const { isSecurity, isDefaultAdmin } = identity;

      userPermissionKeyValuePairs.push(new UserPermissionKeyValue(UserPermissionKeys.isSecurity, isSecurity));
      userPermissionKeyValuePairs.push(new UserPermissionKeyValue(UserPermissionKeys.isDefaultAdmin, isDefaultAdmin));
    }
  }

  private setDefaultUserPermissionKeyValueList(): UserPermissionKeyValue[] {
    const userPermissionKeyValuePairs = [];
    const keys = Object.values(new UserPermissionKeys()).map((value: string) => value);

    keys.forEach(k => {
      userPermissionKeyValuePairs.push(new UserPermissionKeyValue(k, false));
    });

    return userPermissionKeyValuePairs;
  }

  public checkUserHasPermissions(requiredPermission: string): boolean {
    const userPermissionKeyValuePairs = this.userPermissionKeyValuePairsSource.value;
    const foundKeyValue = userPermissionKeyValuePairs ? userPermissionKeyValuePairs.find(e => e.key === requiredPermission) : undefined;

    return foundKeyValue ? foundKeyValue.value : false;
  }

  routeHasPermission(route: ActivatedRouteSnapshot): boolean {
    // If any of the required properties are missing then disallow
    if (!route) {
      return false;
    }

    const routeRolePermissions = route.data['permission'];
    const routeClaimPermissions = route.data['claimPermission'];

    const hasRolePermission = !routeRolePermissions ? true : this.checkRouteHasUserPermissions(routeRolePermissions.only);
    const hasSecurityClaim = !routeClaimPermissions ? true : this.hasSecurityClaim(routeClaimPermissions.only);

    return hasRolePermission && hasSecurityClaim;
  }

  private checkRouteHasUserPermissions(requiredPermissions: string[]): boolean {
    // If no permissions are specified then allow
    if (!requiredPermissions) {
      return true;
    }

    const userPermissionKeyValuePairs = this.userPermissionKeyValuePairsSource.value;

    if (!userPermissionKeyValuePairs || userPermissionKeyValuePairs.length === 0) {
      return false;
    }

    return requiredPermissions.some(permission => this.checkUserHasPermissions(permission));
  }

  private hasSecurityClaim(claimPermission: string[]): boolean {
    if (!claimPermission.includes('isSecurity')) {
      // route should use only isSecurity claim
      return true;
    }

    return this.Permissions.isSecurity;
  }
}
